/* -------------------------------------------------------------------------------------------------
 * Theme Builder Constants
 * Ported from the HeroUI official theme builder reference.
 * -----------------------------------------------------------------------------------------------*/

import airbnbImg from "../../assets/airbnb.png";
import blackImg from "../../assets/black.png";
import coinbaseImg from "../../assets/coinbase.png";
import defaultImg from "../../assets/default.png";
import discordImg from "../../assets/discord.png";
import lavenderImg from "../../assets/lavender.png";
import mintImg from "../../assets/mint.png";
import netflixImg from "../../assets/netflix.png";
import rabbitImg from "../../assets/rabbit.png";
import skyImg from "../../assets/sky.png";
import spotifyImg from "../../assets/spotify.png";

export const DEFAULT_BASE = 0.0015 as const;

/* -------------------------------------------------------------------------------------------------
 * Types
 * -----------------------------------------------------------------------------------------------*/

export const radiusIds = [
  "none",
  "extra-small",
  "small",
  "medium",
  "large",
  "extra-large",
] as const;

export type RadiusId = (typeof radiusIds)[number];

export const fontIds = [
  "inter",
  "figtree",
  "hanken-grotesk",
  "geist",
  "dm-sans",
  "public-sans",
  "google-sans",
  "bricolage-grotesque",
  "varela-round",
  "fraunces",
  "ibm-plex-mono",
  "fredoka",
  "jetbrains-mono",
  "instrument-sans",
] as const;

export type FontId = (typeof fontIds)[number];

export interface BuilderParams {
  hue: number;
  chroma: number;
  lightness: number;
  base: number;
  radius: RadiusId;
  formRadius: RadiusId;
  fontFamily: FontId;
}

export interface SemanticColorOverride {
  color: string;
  foreground?: string;
}

export interface SemanticOverrides {
  light?: {
    accentForeground?: string;
    danger?: SemanticColorOverride;
    success?: SemanticColorOverride;
    warning?: SemanticColorOverride;
  };
  dark?: {
    accentForeground?: string;
    danger?: SemanticColorOverride;
    success?: SemanticColorOverride;
    warning?: SemanticColorOverride;
  };
}

export interface PresetTheme {
  id: string;
  label: string;
  image?: string;
  hue: number;
  chroma: number;
  lightness: number;
  base: number;
  radius: RadiusId;
  formRadius: RadiusId;
  fontFamily: FontId;
  semanticOverrides?: SemanticOverrides;
}

/* -------------------------------------------------------------------------------------------------
 * Defaults
 * -----------------------------------------------------------------------------------------------*/

export const DEFAULT_BUILDER_PARAMS: BuilderParams = {
  base: DEFAULT_BASE,
  chroma: 0.195,
  fontFamily: "inter",
  formRadius: "large",
  hue: 253.83,
  lightness: 0.6204,
  radius: "medium",
} as const;

/* -------------------------------------------------------------------------------------------------
 * Adaptive Colors
 * Colors that need different values in light vs dark modes (e.g. pure black/white).
 * -----------------------------------------------------------------------------------------------*/

export const adaptiveColors: Record<string, {light: string; dark: string}> = {
  "oklch(0 0 0)": {dark: "oklch(0.9848 0 0)", light: "oklch(0 0 0)"},
};

/* -------------------------------------------------------------------------------------------------
 * Font Configurations
 * -----------------------------------------------------------------------------------------------*/

export interface FontConfig {
  id: FontId;
  label: string;
  variable: string;
  cdnUrl: string;
}

export const fonts: FontConfig[] = [
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap",
    id: "inter",
    label: "Inter",
    variable: "--font-inter",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Figtree:wght@300..900&display=swap",
    id: "figtree",
    label: "Figtree",
    variable: "--font-figtree",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@100..900&display=swap",
    id: "hanken-grotesk",
    label: "Hanken Grotesk",
    variable: "--font-hanken-grotesk",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Geist:wght@100..900&display=swap",
    id: "geist",
    label: "Geist",
    variable: "--font-geist",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@100..900&display=swap",
    id: "dm-sans",
    label: "DM Sans",
    variable: "--font-dm-sans",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Public+Sans:wght@100..900&display=swap",
    id: "public-sans",
    label: "Public Sans",
    variable: "--font-public-sans",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Google+Sans:wght@100..900&display=swap",
    id: "google-sans",
    label: "Google Sans",
    variable: "--font-google-sans",
  },
  {
    cdnUrl:
      "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@200..800&display=swap",
    id: "bricolage-grotesque",
    label: "Bricolage Grotesque",
    variable: "--font-bricolage-grotesque",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Varela+Round&display=swap",
    id: "varela-round",
    label: "Varela Round",
    variable: "--font-varela-round",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Fraunces:wght@100..900&display=swap",
    id: "fraunces",
    label: "Fraunces",
    variable: "--font-fraunces",
  },
  {
    cdnUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap",
    id: "ibm-plex-mono",
    label: "IBM Plex Mono",
    variable: "--font-ibm-plex-mono",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Fredoka:wght@300..700&display=swap",
    id: "fredoka",
    label: "Fredoka",
    variable: "--font-fredoka",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@100..800&display=swap",
    id: "jetbrains-mono",
    label: "JetBrains Mono",
    variable: "--font-jetbrains-mono",
  },
  {
    cdnUrl: "https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400..700&display=swap",
    id: "instrument-sans",
    label: "Instrument Sans",
    variable: "--font-instrument-sans",
  },
];

export const fontMap = Object.fromEntries(fonts.map((f) => [f.id, f])) as Record<
  FontId,
  FontConfig
>;

/* -------------------------------------------------------------------------------------------------
 * Radius Options
 * -----------------------------------------------------------------------------------------------*/

export const radiusOptions: Array<{
  cssValue: string;
  description: string;
  id: RadiusId;
  label: string;
}> = [
  {cssValue: "0", description: "none", id: "none", label: "-"},
  {cssValue: "0.125rem", description: "extra small", id: "extra-small", label: "XS"},
  {cssValue: "0.25rem", description: "small", id: "small", label: "S"},
  {cssValue: "0.5rem", description: "medium", id: "medium", label: "M"},
  {cssValue: "0.75rem", description: "large", id: "large", label: "L"},
];

export const formRadiusOptions: Array<{
  cssValue: string;
  description: string;
  id: RadiusId;
  label: string;
}> = [
  {cssValue: "0", description: "none", id: "none", label: "-"},
  {cssValue: "0.125rem", description: "extra small", id: "extra-small", label: "XS"},
  {cssValue: "0.25rem", description: "small", id: "small", label: "S"},
  {cssValue: "0.5rem", description: "medium", id: "medium", label: "M"},
  {cssValue: "0.75rem", description: "large", id: "large", label: "L"},
  {cssValue: "1rem", description: "extra large", id: "extra-large", label: "XL"},
];

export const radiusCssMap = Object.fromEntries(
  formRadiusOptions.map((r) => [r.id, r.cssValue]),
) as Record<RadiusId, string>;

/* -------------------------------------------------------------------------------------------------
 * Theme Presets
 * Ported directly from theme-values.ts in the reference repository.
 * -----------------------------------------------------------------------------------------------*/

export const THEME_PRESETS: PresetTheme[] = [
  {
    base: DEFAULT_BASE,
    chroma: 0.195,
    fontFamily: "inter",
    formRadius: "large",
    hue: 253.83,
    id: "default",
    image: defaultImg,
    label: "Default",
    lightness: 0.6204,
    radius: "medium",
  },
  {
    base: DEFAULT_BASE,
    chroma: 0.16,
    fontFamily: "inter",
    formRadius: "large",
    hue: 225,
    id: "sky",
    image: skyImg,
    label: "Sky",
    lightness: 0.78,
    radius: "medium",
  },
  {
    base: DEFAULT_BASE,
    chroma: 0.13,
    fontFamily: "inter",
    formRadius: "large",
    hue: 305,
    id: "lavender",
    image: lavenderImg,
    label: "Lavender",
    lightness: 0.77,
    radius: "medium",
  },
  {
    base: DEFAULT_BASE,
    chroma: 0.12,
    fontFamily: "inter",
    formRadius: "large",
    hue: 155,
    id: "mint",
    image: mintImg,
    label: "Mint",
    lightness: 0.82,
    radius: "medium",
  },
  {
    base: 0,
    chroma: 0.2349,
    fontFamily: "inter",
    formRadius: "extra-small",
    hue: 27.99,
    id: "netflix",
    image: netflixImg,
    label: "Netflix",
    lightness: 0.5814,
    radius: "extra-small",
    semanticOverrides: {
      dark: {
        danger: {color: "oklch(0.4964 0.1994 28.56)"},
        success: {color: "oklch(0.7677 0.1899 148.1)"},
        warning: {color: "oklch(0.8239 0.153 74.6)"},
      },
      light: {
        danger: {color: "oklch(0.4823 0.1938 27.64)"},
        success: {color: "oklch(0.5148 0.1337 146.82)"},
        warning: {color: "oklch(0.561 0.116571 78.9352)"},
      },
    },
  },
  {
    base: 0,
    chroma: 0,
    fontFamily: "inter",
    formRadius: "small",
    hue: 0,
    id: "uber",
    image: blackImg,
    label: "Uber",
    lightness: 0,
    radius: "small",
    semanticOverrides: {
      dark: {
        danger: {color: "oklch(0.7044 0.1872 23.19)"},
        success: {color: "oklch(0.6514 0.1321 156.22)"},
        warning: {color: "oklch(0.8803 0.1348 86.06)"},
      },
      light: {
        danger: {color: "oklch(0.573 0.2249 21.97)"},
        success: {color: "oklch(0.6277 0.1604 153.06)"},
        warning: {color: "oklch(0.8446 0.1525 80.6)"},
      },
    },
  },
  {
    base: 0.002,
    chroma: 0.2124,
    fontFamily: "inter",
    formRadius: "extra-small",
    hue: 148.67,
    id: "spotify",
    image: spotifyImg,
    label: "Spotify",
    lightness: 0.7697,
    radius: "medium",
    semanticOverrides: {
      dark: {
        danger: {color: "oklch(0.5931 0.2338 25.42)"},
        success: {color: "oklch(0.7697 0.2124 148.67)"},
        warning: {color: "oklch(0.7921 0.1626 67.42)"},
      },
      light: {
        danger: {color: "oklch(0.5509 0.2166 25.29)"},
        success: {color: "oklch(0.6072 0.1647 149.02)"},
        warning: {color: "oklch(0.6972 0.1687 54.22)"},
      },
    },
  },
  {
    base: 0.002,
    chroma: 0.2628,
    fontFamily: "inter",
    formRadius: "extra-small",
    hue: 262.87,
    id: "coinbase",
    image: coinbaseImg,
    label: "Coinbase",
    lightness: 0.5282,
    radius: "medium",
    semanticOverrides: {
      dark: {
        danger: {color: "oklch(0.6545 0.2145 22.31)"},
        success: {color: "oklch(0.7574 0.180554 156.931)"},
        warning: {color: "oklch(0.8095 0.1119 61.69)"},
      },
      light: {
        danger: {color: "oklch(0.5507 0.2062 24)"},
        success: {color: "oklch(0.5438 0.1268 157.17)"},
        warning: {color: "oklch(0.8095 0.1119 61.69)"},
      },
    },
  },
  {
    base: 0,
    chroma: 0.2309,
    fontFamily: "inter",
    formRadius: "large",
    hue: 17.07,
    id: "airbnb",
    image: airbnbImg,
    label: "Airbnb",
    lightness: 0.6579,
    radius: "medium",
    semanticOverrides: {
      dark: {
        accentForeground: "oklch(0.9911 0 0)",
        danger: {color: "oklch(0.5392 0.1816 33.72)", foreground: "oklch(0.9911 0 0)"},
        success: {color: "oklch(0.652 0.114864 185.0749)", foreground: "oklch(0.9911 0 0)"},
        warning: {color: "oklch(0.8197 0.170602 78.4658)"},
      },
      light: {
        accentForeground: "oklch(0.9911 0 0)",
        danger: {color: "oklch(0.5392 0.1816 33.72)", foreground: "oklch(0.9911 0 0)"},
        success: {color: "oklch(0.5573 0.0947 199.48)", foreground: "oklch(0.9911 0 0)"},
        warning: {color: "oklch(0.6904 0.1972 38.75)", foreground: "oklch(0.9911 0 0)"},
      },
    },
  },
  {
    base: 0.01,
    chroma: 0.2091,
    fontFamily: "inter",
    formRadius: "large",
    hue: 273.85,
    id: "discord",
    image: discordImg,
    label: "Discord",
    lightness: 0.5774,
    radius: "small",
    semanticOverrides: {
      dark: {
        danger: {color: "oklch(0.6318 0.2075 24.57)"},
        success: {color: "oklch(0.8548 0.1967 150.16)"},
        warning: {color: "oklch(0.9218 0.1571 99.87)"},
      },
      light: {
        danger: {color: "oklch(0.5884 0.1993 24.39)"},
        success: {color: "oklch(0.532 0.1238 151.57)"},
        warning: {color: "oklch(0.9218 0.1571 99.87)"},
      },
    },
  },
  {
    base: 0.01,
    chroma: 0.2232,
    fontFamily: "inter",
    formRadius: "extra-large",
    hue: 36.66,
    id: "rabbit",
    image: rabbitImg,
    label: "Rabbit",
    lightness: 0.6678,
    radius: "medium",
    semanticOverrides: {
      dark: {
        danger: {color: "oklch(0.6291 0.2565 29.09)"},
        success: {color: "oklch(0.7113 0.2043 140.81)"},
      },
      light: {
        danger: {color: "oklch(0.6291 0.2565 29.09)"},
        success: {color: "oklch(0.7113 0.2043 140.81)"},
      },
    },
  },
];
